import * as Common from "@sp-crm/core";
import {
    BadArgumentException,
    CalendarDate,
    CustomListItemId,
    displayName,
    formatDateNoTime,
    Maybe,
    PlacementId,
} from "@sp-crm/core";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    Row,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { SortDown, SortUp } from "components/ui/icon";
import { ReferralBasicsFragment } from "generated/graphql";
import React, { useMemo } from "react";
import { useHistory } from "react-router";
import { useCustomList } from "store/selectors/bridge";
import { useTenantSettings } from "store/selectors/settings";
import { ResponsiveMode } from "../../../store/reducers/responsive";
import { ClientPersonaLink } from "./client-persona-link";
import { IClientDataSource } from "./list-client-types";

interface Props {
    clients: ReferralBasicsFragment["placement"][];
    showAssignedToColumn: boolean;
    users: { [id: string]: Common.User };
    responsiveMode?: ResponsiveMode;
    tableShort?: boolean;
    sourceType: IClientDataSource;
}

interface DisplayableClient {
    clientId: PlacementId;
    primaryName: string;
    clientName: string;
    bestContactName: string;
    dateAdded: Date;
    statusListItemId: CustomListItemId;
    nextTask: string | null;
    nextTaskDueDate: Date | null;
    assignedTo: string | null;
    moveIn: CalendarDate | null;
    moveClassification: null | "estimated" | "planned" | "actual";
    serial: number | null | undefined;
}

const caseInsensitiveAlpha =
    (accessor: keyof DisplayableClient) =>
    (rowA: Row<DisplayableClient>, rowB: Row<DisplayableClient>): number => {
        const a = Maybe.fromValue(rowA)
            .map(row => row.original)
            .map(r => r[accessor])
            .map(x => x.toString())
            .map(x => x.toLocaleLowerCase())
            .getOrElse("");
        const b = Maybe.fromValue(rowB)
            .map(row => row.original)
            .map(r => r[accessor])
            .map(x => x.toString())
            .map(x => x.toLocaleLowerCase())
            .getOrElse("");
        return a.localeCompare(b);
    };

const forgivingDate =
    (accessor: keyof DisplayableClient) =>
    (rowA: Row<DisplayableClient>, rowB: Row<DisplayableClient>): number => {
        const a = Maybe.fromValue(rowA)
            .map(row => row.original)
            .map(r => r[accessor] as Date)
            .map(x => x.getTime())
            .getOrElse(4099766400000);
        const b = Maybe.fromValue(rowB)
            .map(row => row.original)
            .map(r => r[accessor] as Date)
            .map(x => x.getTime())
            .getOrElse(4099766400000);
        return a - b;
    };

const forgivingCalendarDate =
    (accessor: keyof DisplayableClient) =>
    (rowA: Row<DisplayableClient>, rowB: Row<DisplayableClient>): number => {
        const a = Maybe.fromValue(rowA)
            .map(row => row.original)
            .map(r => r[accessor] as CalendarDate)
            .map(cd => cd.toDate())
            .map(x => x.getTime())
            .getOrElse(4099766400000);
        const b = Maybe.fromValue(rowB)
            .map(row => row.original)
            .map(r => r[accessor] as CalendarDate)
            .map(cd => cd.toDate())
            .map(x => x.getTime())
            .getOrElse(4099766400000);
        return a - b;
    };

const statusSort = (
    compareListItems: (a: CustomListItemId, b: CustomListItemId) => number,
): ((a: Row<DisplayableClient>, b: Row<DisplayableClient>) => number) => {
    return (rowA: Row<DisplayableClient>, rowB: Row<DisplayableClient>): number => {
        const a = Maybe.fromValue(rowA)
            .map(row => row.original)
            .map(r => r.statusListItemId)
            .getOrElse(null);

        const b = Maybe.fromValue(rowB)
            .map(row => row.original)
            .map(r => r.statusListItemId)
            .getOrElse(null);

        return compareListItems(a, b);
    };
};

export const ListClient: React.FC<Props> = props => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "dateAdded", desc: true },
    ]);
    const { users } = props;
    const { settings } = useTenantSettings();
    const showClientFirst = settings.showClientFirst;
    const showClientSerial = settings.showClientSerial;
    const { getListItemName, compareListItems } = useCustomList(
        Common.CustomListKey.ClientStatus,
    );
    const clientToDisplayable = React.useCallback(
        (c: ReferralBasicsFragment["placement"]): DisplayableClient => {
            if (!c) {
                throw new BadArgumentException(
                    "ListClientModern.annotatedToDisplayable, client cannot be falsy",
                );
            }
            const nextTask = c.nextTask;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            const moveDetails: any = { moveIn: null, moveClassification: null };
            if (c.moveTimeline) {
                moveDetails.moveClassification = "Estimated";
                moveDetails.moveIn = CalendarDate.parse(c.moveTimeline).getOrElse(null);
            }
            if (c.moveTimelineActual) {
                moveDetails.moveClassification = "Actual";
                moveDetails.moveIn = CalendarDate.parse(c.moveTimelineActual).getOrElse(
                    null,
                );
            }
            return {
                primaryName: showClientFirst ? (c.name ?? "") : c.bestContactName || "",
                clientName: c.name ?? "",
                bestContactName: c.bestContactName ?? "",
                clientId: c.id,
                statusListItemId: c.statusListItemId,
                dateAdded: new Date(c.dateAdded),
                nextTask: nextTask?.text || "",
                nextTaskDueDate: nextTask?.compositeDueDateTime
                    ? new Date(nextTask.compositeDueDateTime)
                    : null,
                ...moveDetails,
                assignedTo:
                    users && users[c.assignedUserId]
                        ? displayName(users[c.assignedUserId])
                        : "unassigned",
                serial: c.serial && c.serial !== 0 ? c.serial : undefined,
            };
        },
        [showClientFirst, users],
    );

    const columns: ColumnDef<DisplayableClient>[] = React.useMemo(() => {
        const allColumns: (ColumnDef<DisplayableClient> & { show: boolean })[] = [
            {
                accessorKey: "serial",
                header: "ID",
                show: showClientSerial,
            },
            {
                show: true,
                accessorKey: "primaryName",
                header: "Client",
                sortingFn: caseInsensitiveAlpha("primaryName"),
                cell: info => (
                    <ClientPersonaLink
                        client={{
                            appLink: `/clients/show/${info.row.original.clientId}`,
                            name: info.row.original.clientName,
                            bestContactName: info.row.original.bestContactName,
                        }}
                    />
                ),
            },
            {
                show: true,
                accessorKey: "dateAdded",
                cell: info => <div>{formatDateNoTime(info.getValue() as Date)}</div>,
                header: "Date Created",
                sortingFn: forgivingDate("dateAdded"),
            },
            {
                show: true,
                accessorKey: "statusListItemId",
                header: "Status",
                sortingFn: statusSort(compareListItems),
                cell: info => (
                    <div>{getListItemName(info.getValue() as CustomListItemId)}</div>
                ),
            },
            {
                show: true,
                accessorKey: "nextTaskDueDate",
                cell: info => (
                    <div>
                        <div className="text-sm truncate text-gray-900">
                            {info.row.original.nextTask}
                        </div>
                        <div className="text-sm text-gray-500">
                            {formatDateNoTime(info.getValue() as Date)}
                        </div>
                    </div>
                ),
                header: "Next Task",
                sortingFn: forgivingDate("nextTaskDueDate"),
            },
            {
                show: true,
                accessorKey: "moveIn",
                cell: info => (
                    <div>
                        <div className="text-sm text-gray-900">
                            {(info.getValue() as CalendarDate)?.humanFriendly()}
                        </div>
                        <div className="text-sm text-gray-500">
                            {info.row.original.moveClassification}
                        </div>
                    </div>
                ),
                header: "Target Move-In",
                sortingFn: forgivingCalendarDate("moveIn"),
            },
            {
                show: true,
                accessorKey: "assignedTo",
                header: "Assigned to",
            },
        ];
        return allColumns.filter(x => x.show) as ColumnDef<DisplayableClient>[];
    }, [getListItemName, showClientSerial, compareListItems]);
    const memoizedClients = useMemo(
        () => props.clients.map(clientToDisplayable),
        [props.clients, clientToDisplayable],
    );

    const table = useReactTable({
        data: memoizedClients,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const router = useHistory();

    return (
        <div className="shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                    onClick={header.column.getToggleSortingHandler()}>
                                    <div className="flex items-center cursor-pointer">
                                        <span className="-ml-4 mr-1 w-3">
                                            {header.column.getIsSorted() ? (
                                                header.column.getIsSorted() === "desc" ? (
                                                    <SortDown />
                                                ) : (
                                                    <SortUp />
                                                )
                                            ) : (
                                                ""
                                            )}
                                        </span>
                                        <span>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                        </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.map(row => {
                        return (
                            <tr
                                key={row.id}
                                onClick={() =>
                                    router.push(`/clients/show/${row.original.clientId}`)
                                }
                                className={`hover:bg-gray-100 cursor-pointer`}>
                                {row.getVisibleCells().map(cell => {
                                    return (
                                        <td
                                            key={cell.id}
                                            className="px-6 py-4 truncate max-w-xs">
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
